import { Head, Link, usePage } from "@inertiajs/react";
import { LandingNav, LandingFooter } from "@/components/landing/LandingChrome";
import LandingHero from "@/components/landing/LandingHero";
import PaperTear from "@/components/landing/PaperTear";
import FaqList from "@/components/landing/FaqList";
import FlashStack from "@/components/layout/FlashStack";
import Panel from "@/components/ui/Panel";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import SpeechHeading from "@/components/ui/SpeechHeading";
import SpeechBubble from "@/components/ui/SpeechBubble";
import { ButtonLink } from "@/components/ui/Button";
import drink from "@/assets/brand/drink.png";
import checker from "@/assets/brand/checker-tile.webp";
import roomPhoto from "@/assets/photos/hero-13.webp";
import AboutBoba from "@/components/landing/AboutBoba";
import { WHAT_YOU_DO, DEFAULT_FAQ } from "@/utils/programCopy";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { PublicProps } from "@/types";

type Stats = {
  city_count: number;
  country_count: number;
  event_count: number;
  attendee_count: number;
};

// Round down to the nearest ten. These are live counts on a page people link
// to, so a number that only ever understates stays true between page loads.
// Under ten there is nothing to round to, so say the real one.
function roundedDown(n: number) {
  return n >= 10 ? `${Math.floor(n / 10) * 10}+` : `${n}`;
}

// "50+ events across 23 countries, over 350 signed up so far." Each clause is
// dropped rather than printed empty, so a thin week never reads as "0 signed
// up" or "across 0 countries".
function reachLine(stats: Stats, over = false) {
  const countries =
    stats.country_count > 0
      ? `across ${stats.country_count} ${stats.country_count === 1 ? "country" : "countries"}`
      : null;
  const line = [`${roundedDown(stats.event_count)} events`, countries]
    .filter(Boolean)
    .join(" ");

  if (stats.attendee_count < 10) return `${line}.`;
  // Round off the count below itself, not to the nearest ten: at exactly 360,
  // "over 360" is a false claim.
  const signups = Math.floor((stats.attendee_count - 1) / 10) * 10;
  return over
    ? `${line}, and over ${signups} of you signed up.`
    : `${line}, over ${signups} signed up so far.`;
}

export default function Landing({
  signed_in,
  upcoming,
  stats,
}: {
  signed_in: boolean;
  upcoming: SatelliteEvent[];
  stats: Stats;
}) {
  const { props } = usePage<PublicProps>();
  const program = props.program;
  // Closing the organizer boards is how HQ ends a season (see LandingHero).
  const seasonOver = !program.organizer_boards_open;

  return (
    <>
      <Head title={program.name} />

      {/* The hero takes an email address, so the confirmation has to land
          somewhere the person can see it. */}
      <FlashStack />

      <div id="top" className="text-ink min-h-screen bg-white antialiased">
        <LandingNav signedIn={signed_in} />

        <LandingHero cityCount={stats.city_count} />

        {/* What is it. The Figma pairs the speech-bubble title with the drink,
            then an attached photo beside the bubble that answers it. */}
        <section id="what" className="bg-surface px-4 py-16 sm:px-6">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
            <div className="flex items-end gap-4">
              <SpeechHeading title="What is a Boba Bash?" level="h2" />
              <img
                src={drink}
                alt=""
                aria-hidden
                className="h-20 w-auto shrink-0 sm:h-24"
              />
            </div>

            <div className="flex flex-col items-start gap-6 md:flex-row">
              <img
                src={roomPhoto}
                alt="Teenagers building things around a long table at a Hack Club event"
                className="border-line shadow-card h-64 w-full shrink-0 border-2 object-cover md:h-80 md:w-72"
              />

              <SpeechBubble
                className="flex-1"
                bodyClassName="text-body text-ink-muted flex flex-col gap-4"
              >
                <AboutBoba />
              </SpeechBubble>
            </div>
          </div>
        </section>

        {/* What you do. A full-bleed checkerboard band torn out of the white
            page above and below it, exactly as the Figma stages it. */}
        <section
          className="bg-white bg-repeat"
          style={{ backgroundImage: `url(${checker})`, backgroundSize: "96px" }}
        >
          <PaperTear />

          <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-4 py-10 sm:px-6">
            <SpeechHeading title="What do you do at a Boba Bash?" level="h2" />

            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-3">
              {WHAT_YOU_DO.map((step, i) => (
                <Card
                  key={step.title}
                  // The Figma lifts the middle card out of line with the other
                  // two. Only once there are three columns to break.
                  className={i === 1 ? "sm:-translate-y-6" : undefined}
                  media={
                    <img
                      src={step.art}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="aspect-square w-full object-cover"
                    />
                  }
                >
                  <h3 className="font-display text-title text-ink text-center font-bold">
                    {step.title}
                  </h3>
                  <p className="text-caption text-ink-muted mt-2 text-center">
                    {step.body}
                  </p>
                </Card>
              ))}
            </div>

            <Link
              href="/events"
              className="font-display text-heading text-sunset-orange font-bold italic underline decoration-2 underline-offset-4"
            >
              {seasonOver
                ? "see every city that ran one"
                : "find a boba bash in your city"}{" "}
              &gt;&gt;&gt;
            </Link>
          </div>

          <PaperTear flip />
        </section>

        {/* Where to go. The one section that has to carry real data: this is
            the whole point of the participant side. */}
        <section id="events" className="bg-surface px-4 py-16 sm:px-6">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
            <SpeechHeading
              title={
                seasonOver ? "Where it happened" : "Where is one happening?"
              }
              level="h2"
              subtitle={
                seasonOver
                  ? `${reachLine(stats, true)} Thank you to every organizer and everyone who turned up.`
                  : stats.event_count > 0
                    ? `${reachLine(stats)} Signing up takes about a minute, and you don't need an account.`
                    : "Cities are still being confirmed. Leave your email above and we'll tell you the moment yours lands."
              }
              actions={
                <ButtonLink href="/events" variant="secondary">
                  See all events
                </ButtonLink>
              }
            />

            {upcoming.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {upcoming.map((event) => (
                  <Link
                    key={event.id}
                    href={`/events/${event.slug ?? event.id}`}
                  >
                    <Card interactive>
                      <h3 className="font-display text-subheading text-ink font-bold">
                        {event.name}
                      </h3>
                      <p className="text-caption text-ink-muted mt-1">
                        {event.location_label}
                      </p>
                      <p className="text-body text-ink mt-3 font-bold">
                        {event.starts_at_short_label ?? "Date to be announced"}
                      </p>
                      <div className="mt-auto flex flex-wrap gap-2 pt-4">
                        <Badge tone="neutral">
                          {event.rsvp_count ?? 0} going
                        </Badge>
                        {event.full && <Badge tone="warning">Full</Badge>}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Panel title="cities.txt">
                <p className="text-body text-ink-muted">
                  {seasonOver
                    ? "Every Boba Bash this year has happened. Leave your email at the top and we'll tell you when it's back."
                    : "No dates are public yet. Organizers are still booking venues, so leave your email at the top and you'll hear about yours first."}
                </p>
              </Panel>
            )}
          </div>
        </section>

        {/* FAQ. The Figma drops the whole band onto sidecar grid paper. */}
        <section id="faq" className="bg-band-faq relative">
          <PaperTear />

          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(97,51,1,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(97,51,1,.06) 1px, transparent 1px)",
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-12 sm:px-6">
            <div className="flex flex-wrap items-baseline gap-4 border-b-2 border-current/20 pb-2">
              <h2 className="font-display text-display text-ink-muted font-bold">
                FAQ
              </h2>
              <span className="text-subheading text-ink-muted italic">
                frequently asked questions
              </span>
            </div>

            <Panel title="faq.csv" tone="alt">
              <FaqList items={DEFAULT_FAQ} />

              <p className="text-caption text-ink-soft mt-6 text-center">
                Still stuck? Ask us in{" "}
                {program.support_channel_name ? (
                  <a
                    className="font-bold underline"
                    href={program.support_channel_url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    #{program.support_channel_name}
                  </a>
                ) : (
                  "our Slack"
                )}
                , or read the{" "}
                <Link className="font-bold underline" href="/guides">
                  guides
                </Link>
                .
              </p>
            </Panel>
          </div>
        </section>

        <LandingFooter />
      </div>
    </>
  );
}
